import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '../contexts/TranslationContext';
import { behavior, getSubscriptionInfo, Subtask } from '../services/api';
import { enqueueSnackbar } from 'notistack';
import { formatPersianNumber } from '../utils/validation';

export const AdvancedSettingsContent: React.FC = () => {
  const { t } = useTranslation();
  const [subscriptionPlan, setSubscriptionPlan] = useState<'intern' | 'expert' | 'master' | null>(null);
  const canAccessAdvancedSettings = subscriptionPlan === 'expert' || subscriptionPlan === 'master';
  const [advancedBoxes, setAdvancedBoxes] = useState<Array<{ id: string; description: string; reason: string; successIndicator: string }>>([]);
  const [lockedBoxes, setLockedBoxes] = useState<{ [key: string]: boolean }>({});
  const [boxData, setBoxData] = useState<{ [key: string]: { description: string; reason: string; successIndicator: string } }>({});
  const [loadingSubtasks, setLoadingSubtasks] = useState<boolean>(true);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [editingBoxId, setEditingBoxId] = useState<string | null>(null);
  const [editingBoxData, setEditingBoxData] = useState<{
    description: string;
    reason: string;
    successIndicator: string;
  } | null>(null);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [newBoxData, setNewBoxData] = useState<{
    description: string;
    reason: string;
    successIndicator: string;
  }>({
    description: '',
    reason: '',
    successIndicator: '',
  });
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState<boolean>(false);
  const [deletingBoxId, setDeletingBoxId] = useState<string | null>(null);
  const [systemPromptContent, setSystemPromptContent] = useState<string[]>([]);
  const [loadingSystemPrompt, setLoadingSystemPrompt] = useState<boolean>(true);
  const [isSystemPromptLocked, setIsSystemPromptLocked] = useState<boolean>(false);
  const [openSystemPromptEditModal, setOpenSystemPromptEditModal] = useState<boolean>(false);
  const [editingSystemPrompt, setEditingSystemPrompt] = useState<string>('');

  const toggleLockBox = async (boxId: string) => {
    try {
      const newLockedState = !lockedBoxes[boxId];
      setLockedBoxes(prev => ({
        ...prev,
        [boxId]: newLockedState,
      }));
      
      await behavior.toggleSubtaskLock(boxId, newLockedState);
      
      enqueueSnackbar(newLockedState ? t('behavior.advanced_setting.lock_close') : t('behavior.advanced_setting.lock_open'), {
        variant: 'success',
        autoHideDuration: 2000,
      });
    } catch (error: any) {
      setLockedBoxes(prev => ({
        ...prev,
        [boxId]: !prev[boxId],
      }));
      
      console.error('Error toggling subtask lock:', error);
      enqueueSnackbar(t('behavior.advanced_setting.lock_error'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const handleOpenEditModal = (boxId: string) => {
    const currentBoxData = boxData[boxId] || advancedBoxes.find(box => box.id === boxId);
    if (currentBoxData) {
      setEditingBoxId(boxId);
      setEditingBoxData({ 
        description: currentBoxData.description || '',
        reason: currentBoxData.reason || '',
        successIndicator: currentBoxData.successIndicator || '',
      });
      setOpenEditModal(true);
    }
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setEditingBoxId(null);
    setEditingBoxData(null);
  };

  const handleSaveEditModal = async () => {
    if (!editingBoxId || !editingBoxData) return;
    
    try {
      const updateData = {
        subtask_name: editingBoxId,
        description: editingBoxData.description,
        reasoning: editingBoxData.reason,
        success_criteria: editingBoxData.successIndicator,
      };
      
      await behavior.updateSubtask(editingBoxId, updateData);
      
      setBoxData(prev => ({
        ...prev,
        [editingBoxId]: { ...editingBoxData },
      }));
      
      setAdvancedBoxes(prev => 
        prev.map(box => 
          box.id === editingBoxId 
            ? { ...box, ...editingBoxData }
            : box
        )
      );
      
      enqueueSnackbar(t('behavior.advanced_setting.ok_edit'), {
        variant: 'success',
        autoHideDuration: 2000,
      });
      
      handleCloseEditModal();
    } catch (error: any) {
      if (canAccessAdvancedSettings) {
        console.error('Error updating subtask:', error);
        enqueueSnackbar(t('behavior.advanced_setting.error_edit'), {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    }
  };

  const handleChangeEditData = (field: 'description' | 'reason' | 'successIndicator', value: string) => {
    if (!editingBoxData) return;
    setEditingBoxData(prev => prev ? { ...prev, [field]: value } : null);
  };

  const handleOpenAddModal = () => {
    if (advancedBoxes.length >= 10) {
      return;
    }
    setNewBoxData({
      description: '',
      reason: '',
      successIndicator: '',
    });
    setOpenAddModal(true);
  };

  const handleCloseAddModal = () => {
    setOpenAddModal(false);
    setNewBoxData({
      description: '',
      reason: '',
      successIndicator: '',
    });
  };

  const handleSaveNewBox = async () => {
    try {
      await behavior.addSubtask({
        description: newBoxData.description,
        reasoning: newBoxData.reason,
        success_criteria: newBoxData.successIndicator,
        locked: false,
      });
      
      await fetchSubtasks();
      
      handleCloseAddModal();
      
      enqueueSnackbar(t('behavior.advanced_setting.ok_add'), {
        variant: 'success',
        autoHideDuration: 2000,
      });
    } catch (error) {
      enqueueSnackbar(t('behavior.advanced_setting.error_add'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const handleChangeNewBoxData = (field: 'description' | 'reason' | 'successIndicator', value: string) => {
    setNewBoxData(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenDeleteConfirm = (boxId: string) => {
    setDeletingBoxId(boxId);
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setOpenDeleteConfirm(false);
    setDeletingBoxId(null);
  };

  const handleOpenSystemPromptEditModal = () => {
    setEditingSystemPrompt(systemPromptContent.join('\n'));
    setOpenSystemPromptEditModal(true);
  };

  const handleCloseSystemPromptEditModal = () => {
    setOpenSystemPromptEditModal(false);
    setEditingSystemPrompt('');
  };

  const handleSaveSystemPrompt = async () => {
    try {
      await behavior.updateSystemPromptContent(editingSystemPrompt);
      const lines = editingSystemPrompt.split('\n').filter(line => line.trim() !== '');
      setSystemPromptContent(lines);
      handleCloseSystemPromptEditModal();
      enqueueSnackbar(t('behavior.systemPrompt.success'), {
        variant: 'success',
        autoHideDuration: 2000,
      });
    } catch (error) {
      console.error('Error updating system prompt:', error);
      enqueueSnackbar(t('behavior.systemPrompt.error'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingBoxId) return;
    
    try {
      await behavior.deleteSubtask(deletingBoxId);
      
      setAdvancedBoxes(prev => prev.filter(box => box.id !== deletingBoxId));
      
      setBoxData(prev => {
        const newData = { ...prev };
        delete newData[deletingBoxId];
        return newData;
      });
      
      setLockedBoxes(prev => {
        const newLocked = { ...prev };
        delete newLocked[deletingBoxId];
        return newLocked;
      });
      
      enqueueSnackbar(t('behavior.advanced_setting.ok_delete'), {
        variant: 'success',
        autoHideDuration: 2000,
      });
      
      handleCloseDeleteConfirm();
    } catch (error: any) {
      console.error('Error deleting subtask:', error);
      enqueueSnackbar(t('behavior.advanced_setting.error_delete'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const fetchSubtasks = async () => {
    try {
      setLoadingSubtasks(true);
      const response = await behavior.getSubtasks();
      const subtasks = response.data || [];
      
      const mappedBoxes = subtasks.map((subtask: Subtask) => ({
        id: subtask.id,
        description: subtask.description,
        reason: subtask.reasoning,
        successIndicator: subtask.success_criteria,
      }));
      
      const initialBoxData: { [key: string]: { description: string; reason: string; successIndicator: string } } = {};
      subtasks.forEach((subtask: Subtask) => {
        initialBoxData[subtask.id] = {
          description: subtask.description,
          reason: subtask.reasoning,
          successIndicator: subtask.success_criteria,
        };
      });
      
      const initialLockedBoxes: { [key: string]: boolean } = {};
      subtasks.forEach((subtask: Subtask) => {
        initialLockedBoxes[subtask.id] = subtask.locked || false;
      });
      
      setAdvancedBoxes(mappedBoxes);
      setBoxData(initialBoxData);
      setLockedBoxes(initialLockedBoxes);
    } catch (error: any) {
      if (canAccessAdvancedSettings) {
        console.error('Error fetching subtasks:', error);
        enqueueSnackbar(t('behavior.advanced_setting.error_fetch'), { 
          variant: 'error',
          autoHideDuration: 4000,
        });
      }
    } finally {
      setLoadingSubtasks(false);
    }
  };

  useEffect(() => {
    fetchSubtasks();
  }, []);

  useEffect(() => {
    const fetchSystemPromptContent = async () => {
      try {
        setLoadingSystemPrompt(true);
        const response = await behavior.getSystemPromptContent();
        setSystemPromptContent(response.data?.system_prompt || []);
        setIsSystemPromptLocked(response.data?.system_prompt_locked || false);
      } catch (error) {
        console.error('Error fetching system prompt content:', error);
        setSystemPromptContent([]);
        setIsSystemPromptLocked(false);
      } finally {
        setLoadingSystemPrompt(false);
      }
    };

    fetchSystemPromptContent();
  }, []);

  useEffect(() => {
    const fetchSubscriptionPlan = async () => {
      try {
        const response = await getSubscriptionInfo();
        if (response.has_active_subscription && response.data) {
          setSubscriptionPlan(response.data.plan);
        } else {
          setSubscriptionPlan(null);
        }
      } catch (error) {
        console.error('Failed to fetch subscription info:', error);
        setSubscriptionPlan(null);
      }
    };

    fetchSubscriptionPlan();
  }, []);

  return (
    <Box>
      <Box>
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              fontSize={{ xs: '1rem', sm: '1.15rem' }}
              sx={{ minWidth: 0 }}
            >
              {t('behavior.advanced_setting.advanced_setting')}
            </Typography>
            <Button
              variant="contained"
              onClick={handleOpenAddModal}
              size="small"
              disabled={advancedBoxes.length >= 10}
              aria-label={t('behavior.advanced_setting.add_subtask_')}
              sx={{
                width: 'max-content !important',
                minWidth: 'unset !important',
                flexShrink: 0,
                fontSize: { xs: '0.75rem !important', sm: '0.9rem !important' },
                px: { xs: 2, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                minHeight: { xs: 34, sm: 'auto' },
                display: 'inline-flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1 },
              }}
            >
              <AddIcon sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 18 }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {t('behavior.advanced_setting.add_subtask_')}
              </Box>
              <Box
                sx={{
                  height: '20px',
                  width: '1px',
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  mx: 0.5,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: 'inherit',
                  fontSize: { xs: '0.75rem', sm: 'inherit' },
                }}
              >
                {formatPersianNumber(10 - advancedBoxes.length)}/{formatPersianNumber(10)}
              </Typography>
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t('behavior.advanced_setting.descriptiontext.des1')}
              <br /><br />
              {t('behavior.advanced_setting.descriptiontext.des2')}
              <br /><br />
              {t('behavior.advanced_setting.descriptiontext.des3')}
            </Typography>
          </Box>

          <Paper sx={{ p: 2, position: 'relative', overflow: 'hidden' }}>
            <Box 
              sx={{
                height: '10px',
                width: '120px',
                backgroundImage: 'url(/images/chain.svg)',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                transform: isSystemPromptLocked ? 'rotate(-45deg) translateX(0)' : 'rotate(-45deg) translateX(-100%)',
                position: 'absolute',
                top: '30px',
                right: '-30px',
                opacity: isSystemPromptLocked ? 1 : 0,
                transition: 'opacity 0.3s linear, transform 0.3s linear',
                pointerEvents: isSystemPromptLocked ? 'auto' : 'none'
              }}
            >
            </Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>{t('behavior.systemPrompt.title')}</Typography>
            <Box sx={{ 
              backgroundColor: isSystemPromptLocked ? '#ECEFF1' : '#ECEFF1',
              p: 2,
              borderRadius: 1,
              borderLeft: `4px solid ${isSystemPromptLocked ? '#B0BEC5' : '#B0BEC5'}`,
              mb: 2,
              transition: 'background-color 0.3s, border-color 0.3s'
            }}>
              {loadingSystemPrompt ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : systemPromptContent.length > 0 ? (
                <Box>
                  {systemPromptContent.map((line, index) => (
                    <Typography 
                      key={index}
                      variant="body1"
                      textAlign={'justify'}
                      sx={{ mb: index < systemPromptContent.length - 1 ? 1 : 0 }}
                    >
                      {line}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body1" textAlign={'justify'}>
                  {t('dashboard.systemPrompt.noSystemPrompt')}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <IconButton
                aria-label="lock"
                onClick={async () => {
                  try {
                    const newLockedState = !isSystemPromptLocked;
                    setIsSystemPromptLocked(newLockedState);
                    
                    await behavior.toggleSystemPromptLock(newLockedState);
                    
                    enqueueSnackbar(newLockedState ? t('behavior.systemPrompt.close') : t('behavior.systemPrompt.open'), {
                      variant: 'success',
                      autoHideDuration: 2000,
                    });
                  } catch (error: any) {
                    setIsSystemPromptLocked(!isSystemPromptLocked);
                    
                    console.error('Error toggling system prompt lock:', error);
                    enqueueSnackbar(t('behavior.systemPrompt.errorlock'), {
                      variant: 'error',
                      autoHideDuration: 3000,
                    });
                  }
                }}
                sx={{
                  backgroundColor: '#FB8C00',
                  color: '#fff',
                  borderRadius: 1,
                  width: 36,
                  height: 32,
                  '& svg': { fontSize: 18 },
                  '&:hover': { backgroundColor: '#EF6C00' },
                }}
              >
                {isSystemPromptLocked ? <LockIcon /> : <LockOpenIcon />}
              </IconButton>
              <IconButton
                aria-label="edit"
                onClick={handleOpenSystemPromptEditModal}
                sx={{
                  backgroundColor: '#2196F3',
                  color: '#fff',
                  borderRadius: 1,
                  width: 36,
                  height: 32,
                  '& svg': { fontSize: 18 },
                  '&:hover': { backgroundColor: '#1976D2' },
                  display: isSystemPromptLocked ? 'none' : 'flex',
                }}
              >
                <EditIcon />
              </IconButton>
            </Box>
          </Paper>

          {loadingSubtasks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : advancedBoxes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                {t('behavior.advanced_setting.zero_fetch')}
              </Typography>
            </Box>
          ) : (
            advancedBoxes.map((box, index) => {
              const isLocked = lockedBoxes[box.id] || false;
              const colorSchemes = [
                { bg: '#E0F2F1', border: '#4DB6AC' },
                { bg: '#E3F2FD', border: '#64B5F6' },
                { bg: '#FFEBEE', border: '#E57373' },
                { bg: '#F3E5F5', border: '#CE93D8' },
              ];
              const colorScheme = colorSchemes[index % colorSchemes.length];
              
              return (
                <Paper key={box.id} sx={{ p: 2, my: 3, position: 'relative', overflow: 'hidden' }}>
                  <Box 
                    sx={{
                      height: '10px',
                      width: '120px',
                      backgroundImage: 'url(/images/chain.svg)',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      transform: isLocked ? 'rotate(-45deg) translateX(0)' : 'rotate(-45deg) translateX(-100%)',
                      position: 'absolute',
                      top: '30px',
                      right: '-30px',
                      opacity: isLocked ? 1 : 0,
                      transition: 'opacity 0.3s linear, transform 0.3s linear',
                      pointerEvents: isLocked ? 'auto' : 'none'
                    }}
                  >
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('behavior.advanced_setting.subtask')}</Typography>
                  <Box sx={{ 
                    backgroundColor: isLocked ? '#ECEFF1' : colorScheme.bg,
                    p: 2,
                    borderRadius: 1,
                    borderLeft: `4px solid ${isLocked ? '#B0BEC5' : colorScheme.border}`,
                    mb: 2,
                    transition: 'background-color 0.3s, border-color 0.3s'
                  }}>
                    <Typography variant="body1" textAlign={'justify'}>{boxData[box.id]?.description || box.description}</Typography>
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('behavior.advanced_setting.subtask_reason')}</Typography>
                  <Box sx={{ 
                    backgroundColor: isLocked ? '#ECEFF1' : colorScheme.bg,
                    p: 2,
                    borderRadius: 1,
                    borderLeft: `4px solid ${isLocked ? '#B0BEC5' : colorScheme.border}`,
                    mb: 2,
                    transition: 'background-color 0.3s, border-color 0.3s'
                  }}>
                    <Typography variant="body1" textAlign={'justify'}>{boxData[box.id]?.reason || box.reason}</Typography>
                  </Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('behavior.advanced_setting.subtask_kpi')}</Typography>
                  <Box sx={{ 
                    backgroundColor: isLocked ? '#ECEFF1' : colorScheme.bg,
                    p: 2,
                    borderRadius: 1,
                    borderLeft: `4px solid ${isLocked ? '#B0BEC5' : colorScheme.border}`,
                    mb: 2,
                    transition: 'background-color 0.3s, border-color 0.3s'
                  }}>
                    <Typography variant="body1" textAlign={'justify'}>{boxData[box.id]?.successIndicator || box.successIndicator}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <IconButton
                      aria-label="lock"
                      onClick={() => toggleLockBox(box.id)}
                      sx={{
                        backgroundColor: '#FB8C00',
                        color: '#fff',
                        borderRadius: 1,
                        width: 36,
                        height: 32,
                        '& svg': { fontSize: 18 },
                        '&:hover': { backgroundColor: '#EF6C00' },
                      }}
                    >
                      {isLocked ? <LockIcon /> : <LockOpenIcon />}
                    </IconButton>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpenEditModal(box.id)}
                      sx={{
                        backgroundColor: '#2196F3',
                        color: '#fff',
                        borderRadius: 1,
                        width: 36,
                        height: 32,
                        '& svg': { fontSize: 18 },
                        '&:hover': { backgroundColor: '#1976D2' },
                        display: isLocked ? 'none' : 'flex',
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleOpenDeleteConfirm(box.id)}
                      sx={{
                        backgroundColor: '#F44336',
                        color: '#fff',
                        borderRadius: 1,
                        width: 36,
                        height: 32,
                        '& svg': { fontSize: 18 },
                        '&:hover': { backgroundColor: '#d32f2f' },
                        display: isLocked ? 'none' : 'flex',
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              );
            })
          )}
        </Paper>
      </Box>

      {/* Edit Modal */}
      <Dialog
        open={openEditModal}
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>ویرایش وظیفه</DialogTitle>
        <DialogContent>
          {editingBoxData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <TextField
                label={t('behavior.advanced_setting.description')}
                multiline
                rows={2}
                fullWidth
                value={editingBoxData.description}
                onChange={(e) => handleChangeEditData('description', e.target.value)}
                variant="outlined"
              />
              <TextField
                label={t('behavior.advanced_setting.reason')}
                multiline
                rows={2}
                fullWidth
                value={editingBoxData.reason}
                onChange={(e) => handleChangeEditData('reason', e.target.value)}
                variant="outlined"
              />
              <TextField
                label={t('behavior.advanced_setting.kpi')}
                multiline
                rows={2}
                fullWidth
                value={editingBoxData.successIndicator}
                onChange={(e) => handleChangeEditData('successIndicator', e.target.value)}
                variant="outlined"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ m: 1 }}>
          <Button onClick={handleCloseEditModal} color="secondary">
            {t('behavior.advanced_setting.cancel')}
          </Button>
          <Button onClick={handleSaveEditModal} variant="contained" color="primary">
            {t('behavior.advanced_setting.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Box Modal */}
      <Dialog
        open={openAddModal}
        onClose={handleCloseAddModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', mt: 1 }}>{t('behavior.advanced_setting.add_subtask')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="subtitle2">{t('behavior.advanced_setting.add_subtask_des')}</Typography>
            <TextField
              label={t('behavior.advanced_setting.subtask')}
              multiline
              rows={2}
              fullWidth
              value={newBoxData.description}
              onChange={(e) => handleChangeNewBoxData('description', e.target.value)}
              variant="outlined"
            />
            <Typography variant="subtitle2">{t('behavior.advanced_setting.add_subtask_des2')}</Typography>
            <TextField
              label={t('behavior.advanced_setting.subtask_reason')}
              multiline
              rows={2}
              fullWidth
              value={newBoxData.reason}
              onChange={(e) => handleChangeNewBoxData('reason', e.target.value)}
              variant="outlined"
            />
            <Typography variant="subtitle2">{t('behavior.advanced_setting.add_subtask_des3')}</Typography>
            <TextField
              label={t('behavior.advanced_setting.subtask_kpi')}
              multiline
              rows={2}
              fullWidth
              value={newBoxData.successIndicator}
              onChange={(e) => handleChangeNewBoxData('successIndicator', e.target.value)}
              variant="outlined"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ my: 1, mx: 1 }}>
          <Button onClick={handleCloseAddModal} color="secondary">
            {t('behavior.advanced_setting.cancel')}
          </Button>
          <Button onClick={handleSaveNewBox} variant="contained" color="primary">
            {t('behavior.advanced_setting.ok')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        sx={{ maxWidth: '500px', margin: '0 auto' }}
        fullWidth
      >
        <DialogTitle>{t('behavior.advanced_setting.delete_subtask.confirm')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('behavior.advanced_setting.delete_subtask.description')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('behavior.advanced_setting.delete_subtask.notback')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} color="secondary">
            {t('behavior.advanced_setting.delete_subtask.cancelit')}
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            {t('behavior.advanced_setting.delete_subtask.deleteit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* System Prompt Edit Modal */}
      <Dialog
        open={openSystemPromptEditModal}
        onClose={handleCloseSystemPromptEditModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('behavior.systemPrompt.editTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={15}
              value={editingSystemPrompt}
              onChange={(e) => setEditingSystemPrompt(e.target.value)}
              variant="outlined"
              placeholder={t('behavior.systemPrompt.placeholder')}
              sx={{ fontFamily: 'monospace' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSystemPromptEditModal} color="secondary">
            {t('behavior.systemPrompt.cancel')}
          </Button>
          <Button onClick={handleSaveSystemPrompt} variant="contained" color="primary">
            {t('behavior.systemPrompt.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

